import { formatCurrency } from '@vrplatform/utils';
import { executeStatementExpression } from '../../expressions';
import { formatPercentage } from '../../utils';
import { groupStatementLinesBy } from '../_helpers';
import type { Statement, StatementSummaryUnion } from '../_types';
import { getNetRevenueLines } from './get-net-revenue-lines';
import { getStatementTemplate } from './get-statement-template';

type FormulaType = 'currency' | 'percentage' | 'number';

export const getFormulaGroupAmount = ({
  formula,
  statements,
  groupedBy,
  groupId,
  type,
}: {
  statements: Statement[];
  formula: string;
  groupedBy: StatementSummaryUnion;
  groupId: string;
  type: FormulaType;
}): { value: number; formattedValue: string } => {
  const template = getStatementTemplate(statements);
  const columns = template?.data.netRevenueSections[0].columns || [];
  const percentageDigits = columns.find(
    (
      column
    ): column is Extract<typeof column, { type: 'formula.percentage' }> =>
      column.type === 'formula.percentage' && column.value === formula
  )?.decimals;

  const netRevenueLines = getNetRevenueLines(statements);

  const currency = statements?.[0]?.currency || 'usd';

  const groupedLines = groupStatementLinesBy({
    lines: netRevenueLines,
    groupedListingAndMonth: false,
    groupedBy,
  });

  try {
    const executed = executeStatementExpression(formula, {
      columns,
      statementLines: groupedLines[groupId] || [],
    });

    if (executed === Infinity || Number.isNaN(executed)) {
      const v = '-';
      return { formattedValue: v, value: 0 };
    } else {
      // TODO: Remove percentage fix
      const hasPercentageFix = formula.split(' ').join('').includes('*100');

      const results: Record<
        FormulaType,
        { formattedValue: string; value: number }
      > = {
        currency: {
          formattedValue: formatCurrency(executed, currency),
          value: executed,
        },
        percentage: {
          formattedValue: formatPercentage(
            hasPercentageFix ? executed / 100 : executed,
            'legacy',
            percentageDigits ?? undefined,
            percentageDigits ?? undefined
          ),
          value: executed,
        },
        number: {
          formattedValue: executed.toString(),
          value: executed,
        },
      };
      return results[type];
    }
  } catch (error: any) {
    console.error(error);
    const message: string = error?.message || 'Failed to parse formula.';
    return {
      formattedValue: message,
      value: 0,
    };
  }
};
